import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useEffect, useRef, useState, useCallback } from "react";
import UserService from "../UserService";

const INITIAL_UPLOAD_PROGRESS = { Current: 0, Total: 0 };

export function useMessageConsumer(vhost) {
    const connectionRef = useRef(null);
    const uploadActiveRef = useRef(false);
    const uploadProgressRef = useRef(0);
    const uploadErrorCountRef = useRef(0);
    const [isConnected, setIsConnected] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(INITIAL_UPLOAD_PROGRESS);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [uploadCompleted, setUploadCompleted] = useState(null);

    const invokeWithErrorReset = useCallback(async (method, ...args) => {
        try {
            const result = await connectionRef.current?.invoke(method, ...args);
            setError(null);
            return result;
        } catch (err) {
            setError(err.toString());
            throw err;
        }
    }, []);

    useEffect(() => {
        if (!vhost) return;

        // remove the /SD from the api url
        let baseURL = process.env.REACT_APP_API_URL;
        let signalRUrl = baseURL + `/hubs/message-controller?vhost=${vhost}`;

        const connection = new HubConnectionBuilder()
            .withUrl(signalRUrl,{
                accessTokenFactory: async () => {
                    await UserService.updateToken(() => Promise.resolve());
                    return UserService.getToken();
                }
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connection.on("MessageReceived", (msg) => {
            setMessage(msg);
            setActionMessage(null);
        });
        connection.on("AckSuccess", (ackMessage) => {
            setMessage(null);
            setError(null);
            setActionMessage(ackMessage ?? null);
        });
        connection.on("RequeueSuccess", () => {
            setMessage(null);
            setError(null);
            setActionMessage(null);
        });
        connection.on("MoveSuccess", () => {
            setMessage(null);
            setError(null);
            setActionMessage(null);
        });
        connection.on("MessageReleased", (err) => {
            setMessage(null);
            setError(err);
        });
        connection.on("Progress", (progress) => {
            uploadProgressRef.current = progress.Current;
            setUploadProgress(progress);
        });
        connection.on("Error", (err) => {
            setError(err);
            setActionMessage(null);
            if (uploadActiveRef.current) {
                const failedFileNumber = uploadProgressRef.current + uploadErrorCountRef.current + 1;
                uploadErrorCountRef.current += 1;
                setUploadErrors((previousErrors) => [
                    ...previousErrors,
                    { number: failedFileNumber, error: err },
                ]);
            }
        });
        connection.on("Completed", (total) => {
            uploadActiveRef.current = false;
            setUploadCompleted(total);
        });

        connection.onreconnecting(() => setIsConnected(false));
        connection.onreconnected(() => setIsConnected(true));
        connection.onclose(() => setIsConnected(false));

        connection.start()
            .then(() => {
                setIsConnected(true);
                setError(null);
            })
            .catch((err) => setError(err.toString()));

        connectionRef.current = connection;

        return () => {
            connection.stop();
        };
    }, [vhost]);

    const startConsuming = useCallback((queueName) => {
        return invokeWithErrorReset("StartConsuming", queueName);
    }, [invokeWithErrorReset]);

    const ack = useCallback(() => {
        return invokeWithErrorReset("Ack");
    }, [invokeWithErrorReset]);

    const sendToSendmessage = useCallback(() => {
        return invokeWithErrorReset("SendToSendmessage");
    }, [invokeWithErrorReset]);

    const removeRetry = useCallback(() => {
        return invokeWithErrorReset("RemoveRetry");
    }, [invokeWithErrorReset]);

    const requeue = useCallback(() => {
        return invokeWithErrorReset("Requeue");
    }, [invokeWithErrorReset]);

    const moveMessage = useCallback((queueName) => {
        return invokeWithErrorReset("MoveMessage", queueName);
    }, [invokeWithErrorReset]);

    const filterMessages = useCallback((queueName, headers) => {
        return invokeWithErrorReset("FilterOutMessage", queueName, headers);
    }, [invokeWithErrorReset]);

    const deliverAllXML = useCallback(async (connectMessages, targetQueue) => {
        uploadActiveRef.current = true;
        uploadProgressRef.current = 0;
        uploadErrorCountRef.current = 0;
        setUploadProgress(INITIAL_UPLOAD_PROGRESS);
        setUploadErrors([]);
        setUploadCompleted(null);

        try {
            const result = await connectionRef.current?.invoke("DeliverAllXML", connectMessages, targetQueue);
            setError(null);
            return result;
        } catch (err) {
            uploadActiveRef.current = false;
            setError(err.toString());
            throw err;
        }
    }, []);

    const resetMessage = useCallback(() => {
        setMessage(null);
    }, []);

    return {
        isConnected,
        message,
        error,
        actionMessage,
        startConsuming,
        ack,
        sendToSendmessage,
        removeRetry,
        requeue,
        moveMessage,
        filterMessages,
        resetMessage,
        deliverAllXML,
        uploadProgress,
        uploadErrors,
        uploadCompleted,
    };
}